// Owner: S5 — form builder, public forms and the submit pipeline (FormBuilder.dc.html).
import { z } from 'zod';
import { ApiKey, Uuid } from './ids';
import { localizedName } from './localized-storage';
import { LocaleCode, languageOf } from './localized-text';
import { Field } from './metamodel';

export const FormSlug = z
  .string()
  .min(3)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, { error: 'Use lowercase letters, digits and dashes' });

export const BlockId = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-zA-Z0-9_-]+$/);

/**
 * Where a form field saves its value. `path` is a field api key of the form's object, or
 * `<relation>.<field>` for a field of a linked record (Company › Fleet size = `company.fleet_size`).
 * `part` splits a text title field into first / last name ("Jonas" + "Brandt" → "Jonas Brandt").
 */
export const FormFieldTarget = z
  .object({
    path: z
      .string()
      .min(1)
      .max(130)
      .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)?$/),
    part: z.enum(['first', 'last']).nullish(),
  })
  .strict();
export type FormFieldTarget = z.infer<typeof FormFieldTarget>;

export const FormFieldLogic = z
  .object({
    showWhen: z.string().max(2000).nullish(),
    requiredWhen: z.string().max(2000).nullish(),
    /** A typed expression; always recalculated by the server. */
    calculate: z.string().max(2000).nullish(),
    defaultValue: z.json().optional(),
    prefillParam: z
      .string()
      .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/)
      .nullish(),
    /** Suppress the control; only its configured default/calculation is accepted. */
    hidden: z.boolean().optional(),
  })
  .strict();
export type FormFieldLogic = z.infer<typeof FormFieldLogic>;

export const FormFieldBlock = z
  .object({
    id: BlockId,
    kind: z.literal('field'),
    target: FormFieldTarget,
    label: z.string().min(1).max(120),
    required: z.boolean().default(false),
    helpText: z.string().max(300).nullish(),
    placeholder: z.string().max(120).nullish(),
    width: z.enum(['full', 'half']).default('full'),
    /**
     * Form logic: expressions of the `form` context (`form.<field>` or the field's key, `params.<name>`
     * for URL parameters), True/false. Shown only while `showWhen` holds (hidden values are not
     * stored); required while `requiredWhen` holds. Checked when the form is saved (expr-language).
     */
    logic: FormFieldLogic.nullish(),
  })
  .strict();
export type FormFieldBlock = z.infer<typeof FormFieldBlock>;

export const FormBlock = z.discriminatedUnion('kind', [
  FormFieldBlock,
  z.object({ id: BlockId, kind: z.literal('heading'), text: z.string().min(1).max(200) }).strict(),
  z.object({ id: BlockId, kind: z.literal('paragraph'), text: z.string().min(1).max(2000) }).strict(),
  z.object({ id: BlockId, kind: z.literal('divider') }).strict(),
  z
    .object({
      id: BlockId,
      kind: z.literal('page_break'),
      title: z.string().max(120).optional(),
      /** Skip this page and its fields unless the condition holds. */
      showWhen: z.string().max(2000).nullish(),
      /** First matching branch after this page; destinations must follow this page. */
      branches: z
        .array(z.object({ when: z.string().min(1).max(2000), goTo: BlockId }))
        .max(20)
        .optional(),
    })
    .strict(),
  z
    .object({
      id: BlockId,
      kind: z.literal('consent'),
      text: z.string().min(1).max(500),
      required: z.boolean().default(true),
      linkLabel: z.string().max(80).nullish(),
      linkUrl: z.url().max(500).nullish(),
    })
    .strict(),
]);
export type FormBlock = z.infer<typeof FormBlock>;

/**
 * The author's texts of a form in one more language (visitors see them when their language matches;
 * the plain texts of the definition are the source). Only what differs is stored: an empty or
 * missing entry shows the source text.
 */
export const FormBlockTranslation = z
  .object({
    label: z.string().max(120).optional(),
    helpText: z.string().max(300).optional(),
    placeholder: z.string().max(120).optional(),
    /** Heading, paragraph and consent text. */
    text: z.string().max(2000).optional(),
    /** Page title (page breaks). */
    title: z.string().max(120).optional(),
    linkLabel: z.string().max(80).optional(),
  })
  .strict();
export type FormBlockTranslation = z.infer<typeof FormBlockTranslation>;

export const FormTranslation = z
  .object({
    title: z.string().max(200).optional(),
    description: z.string().max(1000).optional(),
    submitLabel: z.string().max(60).optional(),
    /** The thank-you message shown after sending. */
    thanks: z.string().max(1000).optional(),
    blocks: z.record(BlockId, FormBlockTranslation).optional(),
  })
  .strict();
export type FormTranslation = z.infer<typeof FormTranslation>;

export const FormDefinition = z
  .object({
    title: z.string().min(1).max(200),
    description: z.string().max(1000).default(''),
    /** Empty = the default button text of the visitor's language (never a stored English word). */
    submitLabel: z.string().max(60).default(''),
    brand: z
      .object({ name: z.string().max(80), letter: z.string().max(2) })
      .strict()
      .default({ name: '', letter: '' }),
    blocks: z.array(FormBlock).max(100),
    /** The same texts in other languages, by language code (`de`, `en`). */
    translations: z.record(LocaleCode, FormTranslation).optional(),
  })
  .strict()
  .superRefine((def, ctx) => {
    const ids = new Set<string>();
    for (const [i, b] of def.blocks.entries()) {
      if (ids.has(b.id))
        ctx.addIssue({ code: 'custom', message: `Duplicate block id "${b.id}"`, path: ['blocks', i] });
      ids.add(b.id);
    }
    for (const [i, b] of def.blocks.entries()) {
      if (b.kind !== 'page_break') continue;
      for (const [j, branch] of (b.branches ?? []).entries()) {
        const destination = def.blocks.findIndex((x) => x.id === branch.goTo && x.kind === 'page_break');
        if (destination <= i)
          ctx.addIssue({
            code: 'custom',
            message: 'Choose a later page',
            path: ['blocks', i, 'branches', j, 'goTo'],
          });
      }
    }
  });
export type FormDefinition = z.infer<typeof FormDefinition>;

function own<T extends string | null | undefined>(value: T, over: string | undefined): T | string {
  return over?.trim() ? over.trim() : value;
}

/**
 * The definition as a visitor in `locale` sees it: the author's translations of that language
 * replace the source texts; whatever has no translation stays as written.
 */
export function translateFormDefinition(def: FormDefinition, locale: string): FormDefinition {
  const tr = def.translations?.[languageOf(locale)];
  if (!tr) return def;
  return {
    ...def,
    title: own(def.title, tr.title),
    description: own(def.description, tr.description),
    submitLabel: own(def.submitLabel, tr.submitLabel),
    blocks: def.blocks.map((b): FormBlock => {
      const o = tr.blocks?.[b.id];
      if (!o) return b;
      switch (b.kind) {
        case 'field':
          return {
            ...b,
            label: own(b.label, o.label),
            helpText: own(b.helpText, o.helpText),
            placeholder: own(b.placeholder, o.placeholder),
          };
        case 'heading':
        case 'paragraph':
          return { ...b, text: own(b.text, o.text) };
        case 'page_break':
          return { ...b, title: own(b.title, o.title) };
        case 'consent':
          return { ...b, text: own(b.text, o.text), linkLabel: own(b.linkLabel, o.linkLabel) };
        default:
          return b;
      }
    }),
  };
}

/** The thank-you text a form starts with; the UIs show it in the viewer's language until the author edits it. */
export const FORM_DEFAULT_THANKS = 'Thanks! We will be in touch shortly.';

export const FormAfterSubmit = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('message'), message: z.string().min(1).max(1000) }).strict(),
  z.object({ kind: z.literal('redirect'), url: z.url().max(500) }).strict(),
  /**
   * Offer a meeting after sending (scheduling, owner feedback F13): the visitor picks a time of the meeting type
   * with this address; name and e-mail address come from the answers. `message` is the thank-you above it.
   */
  z
    .object({
      kind: z.literal('booking'),
      /** Empty until the author has chosen a meeting type: the form then just says thanks. */
      slug: z.union([z.literal(''), z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/)]),
      message: z.string().max(1000).optional(),
    })
    .strict(),
]);
export type FormAfterSubmit = z.infer<typeof FormAfterSubmit>;

/** What happens after sending, for a visitor in `locale`: the thank-you message in their language when the author wrote one. */
export function afterIn(
  after: FormAfterSubmit,
  def: FormDefinition | null | undefined,
  locale: string,
): FormAfterSubmit {
  if (after.kind !== 'message') return after;
  const thanks = def?.translations?.[languageOf(locale)]?.thanks?.trim();
  return thanks ? { kind: 'message', message: thanks } : after;
}

export const FormSettings = z
  .object({
    /** Match an existing record of the form's object by its email field and fill empty fields only. */
    matchExisting: z.boolean().default(true),
    /** Workflow started by the `form.submitted` event (S7 triggers). */
    workflow: z
      .object({ id: z.string().max(80), label: z.string().max(120) })
      .strict()
      .nullish(),
    after: FormAfterSubmit.default({ kind: 'message', message: FORM_DEFAULT_THANKS }),
    /** Hidden honeypot field, minimum fill time and a per-IP rate limit. */
    spamProtection: z.boolean().default(true),
  })
  .strict();
export type FormSettings = z.infer<typeof FormSettings>;

export const FormStatus = z.enum(['draft', 'live', 'closed']);
export type FormStatus = z.infer<typeof FormStatus>;

export const FormDTO = z.object({
  id: Uuid,
  objectKey: z.string(),
  key: z.string(),
  slug: z.string(),
  name: z.string(),
  status: FormStatus,
  draft: FormDefinition,
  published: FormDefinition.nullable(),
  settings: FormSettings,
  publishedSettings: FormSettings.nullable().optional(),
  /** Draft differs from the published definition (or settings changed since publishing). */
  hasUnpublishedChanges: z.boolean(),
  publishedAt: z.string().nullable(),
  submissionsThisMonth: z.number().int(),
  submissionsTotal: z.number().int(),
  version: z.number().int(),
  updatedAt: z.string(),
  /** The signed-in member may change and publish the form (metadata.write); others see it read-only. */
  canEdit: z.boolean().optional(),
});
export type FormDTO = z.infer<typeof FormDTO>;

export const FormKey = z
  .string()
  .min(1)
  .max(63)
  .regex(/^[a-z][a-z0-9-]*$/);

export const CreateFormInput = z
  .object({
    objectKey: ApiKey,
    /** Plain, or one name per language (LocalizedText, plan §8.10). */
    name: localizedName(120),
    key: FormKey.optional(),
    slug: FormSlug.optional(),
    definition: FormDefinition.optional(),
    settings: FormSettings.partial().optional(),
  })
  .strict();
export type CreateFormInput = z.input<typeof CreateFormInput>;

export const UpdateFormInput = z
  .object({
    /** A plain name sets the writer's language when the name is localized. */
    name: localizedName(120),
    slug: FormSlug,
    draft: FormDefinition,
    settings: FormSettings,
    status: z.enum(['live', 'closed']),
    expectedVersion: z.number().int().positive(),
  })
  .partial()
  .strict();
export type UpdateFormInput = z.input<typeof UpdateFormInput>;

/** What the public page renders: the published definition plus render hints of each field. */
export const PublicFormField = z.object({
  blockId: z.string(),
  type: z.string(),
  /** Only fields explicitly present on the published form. */
  field: Field,
  options: z.array(z.object({ key: z.string(), label: z.string() })).optional(),
});
export type PublicFormField = z.infer<typeof PublicFormField>;

export const PublicFormDTO = z.object({
  slug: z.string(),
  name: z.string(),
  definition: FormDefinition,
  fields: z.array(PublicFormField),
  spamProtection: z.boolean(),
  /** Issued at render time; submissions faster than the minimum fill time are rejected as spam. */
  renderedAt: z.number().int(),
  preview: z.boolean(),
  locale: z.enum(['en', 'de']).default('en'),
  /** The public key of the workspace's brand kit (logo, accent and fonts of the form page). */
  brandKey: z.string().nullable().optional(),
});
export type PublicFormDTO = z.infer<typeof PublicFormDTO>;

export const FORM_HONEYPOT = 'website_url_confirm';

export const PublicSubmitInput = z
  .object({
    /** Values keyed by block id. */
    values: z.record(BlockId, z.json()),
    params: z.record(z.string().max(60), z.string().max(2000)).default({}),
    lang: z.enum(['en', 'de']).optional(),
    renderedAt: z.number().int().optional(),
    [FORM_HONEYPOT]: z.string().max(500).optional(),
  })
  .strict();
export type PublicSubmitInput = z.input<typeof PublicSubmitInput>;

export const PublicSubmitResult = z.object({
  ok: z.literal(true),
  after: FormAfterSubmit,
});
export type PublicSubmitResult = z.infer<typeof PublicSubmitResult>;

export const FormSubmissionDTO = z.object({
  id: Uuid,
  status: z.enum(['accepted', 'spam']),
  createdAt: z.string(),
  values: z.record(z.string(), z.unknown()),
  records: z.array(
    z.object({
      objectKey: z.string(),
      id: Uuid,
      title: z.string(),
      action: z.enum(['created', 'updated', 'matched']),
    }),
  ),
  reason: z.string().nullable(),
});
export type FormSubmissionDTO = z.infer<typeof FormSubmissionDTO>;

/** Outbox topic written for every accepted submission (workflows subscribe with `form.submitted`). */
export const FORM_SUBMITTED_TOPIC = 'form.submitted';

export const FormSubmittedPayload = z.object({
  formId: Uuid,
  formKey: z.string(),
  formName: z.string(),
  /** Member who created the form (notified about new submissions). */
  formOwnerId: Uuid.nullable(),
  submissionId: Uuid,
  objectKey: z.string(),
  recordId: Uuid,
  records: FormSubmissionDTO.shape.records,
  /** Workflow chosen under "Then run workflow" (S7 starts it). */
  workflowId: z.string().nullable(),
});
export type FormSubmittedPayload = z.infer<typeof FormSubmittedPayload>;

export const PublishFormInput = z
  .object({ expectedVersion: z.number().int().positive().optional(), note: z.string().max(120).optional() })
  .strict();
export type PublishFormInput = z.input<typeof PublishFormInput>;

export const FormUploadInput = z.object({
  name: z.string().min(1).max(250),
  mime: z.string().max(200).optional(),
});
